import { z } from 'zod';
import {
  EXTERNAL_API_VERSION,
  EXTERNAL_EXAMPLES,
  ExternalAlerts,
  ExternalAlertsQuery,
  ExternalContract,
  type ExternalContractEndpoint,
  type ExternalContractEvent,
  type ExternalContractField,
  type ExternalContractParam,
  ExternalErrors,
  ExternalErrorsQuery,
  ExternalEvent,
  ExternalFormat,
  ExternalServices,
  ExternalServicesQuery,
  ExternalStatus,
  ExternalStatusQuery,
  ExternalStreamQuery,
} from './external';

/**
 * 외부 연동 계약을 zod 스키마에서 만든다 (20회차, C-22).
 *
 * 손으로 적은 표는 스키마와 어긋난다. 그래서 `z.toJSONSchema`로 스키마를 JSON Schema로 펴고,
 * 거기서 필드 경로·타입·필수 여부·설명(`describe()`)·허용 값을 뽑아 평탄한 목록으로 만든다.
 * 예시 값은 `EXTERNAL_EXAMPLES`에서 같은 경로로 따라 들어가 붙인다.
 *
 * 여기에는 node 전용 코드가 없다. 콘솔이 같은 함수로 계약 표를 그릴 수 있다.
 */

/** `z.toJSONSchema`가 내는 것 중 여기서 읽는 부분만 */
interface JsonSchemaNode {
  type?: string | string[];
  properties?: Record<string, JsonSchemaNode>;
  required?: string[];
  items?: JsonSchemaNode;
  description?: string;
  enum?: unknown[];
  const?: unknown;
  pattern?: string;
  oneOf?: JsonSchemaNode[];
  anyOf?: JsonSchemaNode[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** JSON 타입 한 단어. 합집합은 `|`로 잇는다 */
function typeOf(node: JsonSchemaNode): string {
  if (typeof node.type === 'string') return node.type;
  if (Array.isArray(node.type)) return node.type.join('|');
  const variants = node.oneOf ?? node.anyOf;
  if (variants) return [...new Set(variants.map(typeOf))].join('|');
  if (node.enum) return 'string';
  if (node.const !== undefined) return typeof node.const;
  return 'object';
}

function toField(
  name: string,
  node: JsonSchemaNode,
  required: boolean,
  example: unknown,
): ExternalContractField {
  const field: ExternalContractField = {
    name,
    type: typeOf(node),
    required,
    description: node.description ?? '',
  };
  if (node.enum) field.enum = node.enum.map(String);
  if (
    typeof node.const === 'string' ||
    typeof node.const === 'number' ||
    typeof node.const === 'boolean'
  ) {
    field.const = node.const;
  }
  if (node.pattern) field.pattern = node.pattern;
  // 객체·배열은 안쪽 필드가 따로 나오므로 예시는 잎에만 붙인다
  if (example !== undefined && !isRecord(example) && !Array.isArray(example)) {
    field.example = example;
  }
  return field;
}

/**
 * 객체 스키마의 필드를 평탄하게 편다. 배열 안의 객체는 `이름[].필드`, 중첩 객체는 `이름.필드`.
 * `required`는 위쪽이 선택이면 아래도 선택이다(없을 수 있는 객체 안의 필수 필드는 밖에서 보면 선택이다).
 */
function walkObject(
  node: JsonSchemaNode,
  prefix: string,
  example: unknown,
  requiredAbove: boolean,
  out: ExternalContractField[],
): ExternalContractField[] {
  const required = new Set(node.required ?? []);
  for (const [key, child] of Object.entries(node.properties ?? {})) {
    const name = `${prefix}${key}`;
    const isRequired = requiredAbove && required.has(key);
    const value = isRecord(example) ? example[key] : undefined;
    out.push(toField(name, child, isRequired, value));
    if (child.type === 'array' && child.items?.properties) {
      const first = Array.isArray(value) ? value[0] : undefined;
      walkObject(child.items, `${name}[].`, first, isRequired, out);
    } else if (child.properties) {
      walkObject(child, `${name}.`, value, isRequired, out);
    }
  }
  return out;
}

/** 스키마 하나 → 필드 목록. 예시 객체가 있으면 잎 필드에 값을 붙인다 */
export function contractFields(schema: z.ZodType, example?: unknown): ExternalContractField[] {
  const root = z.toJSONSchema(schema) as JsonSchemaNode;
  return walkObject(root, '', example, true, []);
}

/** 쿼리 스키마 → 파라미터 목록. 쿼리는 중첩이 없어 한 겹만 본다 */
export function contractParams(schema: z.ZodType): ExternalContractParam[] {
  const root = z.toJSONSchema(schema) as JsonSchemaNode;
  const required = new Set(root.required ?? []);
  return Object.entries(root.properties ?? {}).map(([name, node]) => {
    const param: ExternalContractParam = {
      name,
      type: typeOf(node),
      required: required.has(name),
      description: node.description ?? '',
    };
    if (node.enum) param.enum = node.enum.map(String);
    return param;
  });
}

/** SSE 이벤트별 설명. `type`이 이벤트 이름이다 */
const EVENT_DESCRIPTION: Record<string, string> = {
  status: '스냅샷이 올 때마다(1초). 인스턴스 목록 전체를 보낸다. 붙는 즉시 한 번 온다',
  error: '오류 요청이 확정될 때. OTLP 배치와 정규화 대기만큼 늦다(실측 약 7초)',
  alert:
    '알림이 발화·해제될 때. 규칙의 채널과 무관하게 전부 온다(통보 notice는 오지 않는다). 붙는 즉시 경보 중인 것을 한 번씩 보낸다',
};

/** 판별 합집합의 변형마다 이벤트 한 종류 */
function contractEvents(): ExternalContractEvent[] {
  const root = z.toJSONSchema(ExternalEvent) as JsonSchemaNode;
  const variants = root.oneOf ?? root.anyOf ?? [];
  return variants.map((variant) => {
    const type = String(variant.properties?.type?.const ?? '');
    const example = (EXTERNAL_EXAMPLES.stream as Record<string, unknown>)[type];
    return {
      type,
      description: EVENT_DESCRIPTION[type] ?? '',
      fields: walkObject(variant, '', example, true, []),
      example,
    };
  });
}

const ALL_FORMATS = ExternalFormat.options;

/**
 * 계약 한 장. `at`만 밖에서 받는다(응답마다 다른 유일한 값).
 * 엔드포인트 순서는 문서(`docs/external-api.md`)와 같다.
 */
export function buildExternalContract(at: number): ExternalContract {
  const endpoints: ExternalContractEndpoint[] = [
    {
      method: 'GET',
      path: '/api/external/status',
      description:
        '인스턴스별 지금 상태. 게이지는 1초 스냅샷, 오류 수는 끝난 요청 5분 창. sse는 오류 집계에서 뺀다',
      kind: 'json',
      formats: ALL_FORMATS,
      rows: 'instances',
      params: contractParams(ExternalStatusQuery),
      fields: contractFields(ExternalStatus, EXTERNAL_EXAMPLES.status),
      example: EXTERNAL_EXAMPLES.status,
    },
    {
      method: 'GET',
      path: '/api/external/errors',
      description: '오류로 끝난 요청 목록. 최근 것이 앞. 메모리 창(5분) 안의 것만',
      kind: 'json',
      formats: ALL_FORMATS,
      rows: 'errors',
      params: contractParams(ExternalErrorsQuery),
      fields: contractFields(ExternalErrors, EXTERNAL_EXAMPLES.errors),
      example: EXTERNAL_EXAMPLES.errors,
    },
    {
      method: 'GET',
      path: '/api/external/alerts',
      description: '지금 경보 중인 알림. 시작이 이른 것이 앞',
      kind: 'json',
      formats: ALL_FORMATS,
      rows: 'alerts',
      params: contractParams(ExternalAlertsQuery),
      fields: contractFields(ExternalAlerts, EXTERNAL_EXAMPLES.alerts),
      example: EXTERNAL_EXAMPLES.alerts,
    },
    {
      method: 'GET',
      path: '/api/external/services',
      description: '서비스별 한 줄 롤업 (26회차). status와 달리 트레이스만 있는 서비스도 나온다',
      kind: 'json',
      formats: ALL_FORMATS,
      rows: 'services',
      params: contractParams(ExternalServicesQuery),
      fields: contractFields(ExternalServices, EXTERNAL_EXAMPLES.services),
      example: EXTERNAL_EXAMPLES.services,
    },
    {
      method: 'GET',
      path: '/api/external/stream',
      description:
        'status·errors·alerts 셋의 변화를 미는 SSE. 25초마다 `: ping` 주석. 이벤트 이름이 data의 type과 같다',
      kind: 'sse',
      formats: [],
      params: contractParams(ExternalStreamQuery),
      fields: [],
      events: contractEvents(),
    },
    {
      method: 'GET',
      path: '/api/external/contract',
      description: '이 계약. 스키마에서 만든 엔드포인트·필드·예시 목록',
      kind: 'json',
      formats: ['json'],
      params: [],
      fields: contractFields(ExternalContract),
    },
  ];
  return {
    version: EXTERNAL_API_VERSION,
    at,
    auth: 'Authorization: Bearer <발급한 토큰>. 토큰은 관제 화면의 관리 > 외부 연동에서 발급한다. SSE만 ?token=도 받는다. 쓸 수 있는 토큰이 하나도 없으면 전체가 503',
    endpoints,
  };
}
